// src/pages/Bitacoras.js
import React, { useState, useCallback, useMemo, memo, useEffect, useRef } from "react";
import {
  Button,
  Container,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  MenuItem,
  Box,
  TextField,
  InputAdornment,
  TableSortLabel,
  Popover,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Tooltip,
  TablePagination
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikTextField from "../components/FormikTextField";
import { useAuth } from "../context/AuthContext";
import { useSftfApi } from "../api/sftfClient";
import {
  NoteAdd as NoteAddIcon, Receipt as ReceiptIcon, Cancel as CancelIcon,
  Search as SearchIcon, FilterList as FilterListIcon, ViewColumn as ViewColumnIcon,
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, FilterAltOff as FilterAltOffIcon,
  DragHandle as DragHandleIcon, Close as CloseIcon, FileDownload as FileDownloadIcon
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import {
  BITACORAS_DEFAULT_COLUMNS,
  construirColumnasBitacoras,
  fusionarConfigColumnas,
} from "../config/bitacorasTableColumns";
import { exportBitacorasToExcel } from "../utils/exportBitacorasToExcel";
import {
  construirParamsConsulta,
  consultaSoloPorFiltrosNoSoportados,
  esRegistroYaFacturado,
} from "../utils/bitacorasQuery";
import { getComparator, stableSort } from "../utils/tableSort";

/**
 * Componente de item de columna memoizado para evitar re-renders innecesarios durante el drag & drop.
 */
/**
 * Componente de diálogo aislado para la configuración de columnas.
 * Al estar aislado, el estado de arrastre no provoca el re-renderizado de la tabla pesada de Bitacoras.
 */
const ColumnConfigDialog = memo(({ open, onClose, onApply, currentConfig, columnasDisponibles }) => {
  const [draft, setDraft] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Sincronizar borrador cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      setDraft([...currentConfig]);
      setIsSaving(false);
    }
  }, [open, currentConfig]);

  const handleToggle = useCallback((id) => {
    setDraft(prev => prev.map(c => {
      if (c.id === id) {
        if (c.disableToggle) return c;
        return { ...c, visible: !c.visible };
      }
      return c;
    }));
  }, []);

  const handleMoveManual = useCallback((index, direction) => {
    setDraft(prev => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      
      // No permitir mover si el elemento actual o el de destino es fijo
      if (prev[index].fixed || prev[targetIndex].fixed) return prev;
      
      const next = [...prev];
      const temp = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = temp;
      return next;
    });
  }, []);

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    
    const sourceIdx = result.source.index;
    const destIdx = result.destination.index;

    setDraft(prev => {
      const items = Array.from(prev);
      const [reorderedItem] = items.splice(sourceIdx, 1);
      items.splice(destIdx, 0, reorderedItem);
      
      // Validar: Si algún elemento marcado como 'fixed' cambió de posición original, abortamos
      const fixedIndexesChanged = items.some((col, idx) => {
        if (col.fixed) {
          // Buscamos si el elemento en este índice en 'prev' era el mismo o si este elemento estaba en otro lado
          return prev[idx]?.id !== col.id;
        }
        return false;
      });

      if (fixedIndexesChanged) return prev;
      return items;
    });
  }, []);

  const handleReset = useCallback(() => {
    // Restablecer significa volver a los valores por defecto (las 46 curadas
    // visibles, las 73 derivadas ocultas), no encender las 119 columnas: eso
    // es lo que ya codifica construirColumnasBitacoras en visibleByDefault.
    setDraft(columnasDisponibles.map(c => ({
      ...c,
      visible: c.disableToggle ? true : c.visibleByDefault !== false,
    })));
  }, [columnasDisponibles]);

  const handleApply = async () => {
    setIsSaving(true);
    // Pequeño delay artificial para que el usuario perciba que se están aplicando los ajustes pesados
    // y vea el spinner de carga.
    await new Promise(resolve => setTimeout(resolve, 600));
    onApply(draft);
    setIsSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Configurar Columnas
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Activa las columnas que desees visualizar y arrástralas para cambiar su orden en la tabla.
        </Typography>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                  {draft.map((col, index) => (
                    <Draggable key={col.id} draggableId={col.id} index={index} isDragDisabled={col.fixed}>
                      {(dragProvided, dragSnapshot) => (
                        <DraggableColumnItem 
                          col={col} 
                          index={index} 
                          totalCount={draft.length}
                          onMove={handleMoveManual}
                          onToggle={handleToggle}
                          provided={dragProvided}
                          snapshot={dragSnapshot}
                        />
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={handleReset} color="warning" variant="outlined" size="small" disabled={isSaving}>
          Restablecer Predeterminados
        </Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSaving ? "Aplicando..." : "Aplicar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

/**
 * Componente principal para la Consulta de Bitácoras y Remisiones.
 */
/**
 * Componente memoizado para la tabla de resultados.
 * Esto evita que el re-renderizado del padre (Bitacoras) al abrir el diálogo
 * obligue a React a procesar toda la tabla innecesariamente.
 */
const BitacorasResultsTable = memo(({ 
  results, 
  activeColumns, 
  orderBy, 
  order, 
  onSort, 
  onFilterClick, 
  onSelectForAdenda, 
  onSelectForFactura, 
  columnFilters,
  onReorderColumns 
}) => {
  // Estado local para el orden de las cabeceras para que el DRAG sea fluido 
  // y no dependa del renderizado pesado de la tabla completa.
  const [localHeaderOrder, setLocalHeaderOrder] = useState(activeColumns);

  // Sincronizar cuando cambian las columnas desde el diálogo
  useEffect(() => {
    setLocalHeaderOrder(activeColumns);
  }, [activeColumns]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    // 1. Actualización optimista inmediata (solo para las cabeceras)
    const newOrder = Array.from(localHeaderOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setLocalHeaderOrder(newOrder);

    // 2. Notificar al padre para que actualice la base de datos/configuración
    onReorderColumns(result.source.index, result.destination.index);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        overflow: 'auto',
        maxHeight: 'calc(100vh - 260px)',
        position: 'relative'
      }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Table sx={{ minWidth: 1200 }} stickyHeader>
          <TableHead>
            <Droppable droppableId="header-droppable" direction="horizontal">
              {(provided) => (
                <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                  {localHeaderOrder.map((headCell, index) => (
                    <Draggable 
                      key={headCell.id} 
                      draggableId={headCell.id} 
                      index={index}
                      isDragDisabled={headCell.fixed}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <TableCell
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          sx={{ 
                            width: headCell.minWidth,
                            minWidth: headCell.minWidth, 
                            bgcolor: dragSnapshot.isDragging ? 'primary.light' : 'background.paper',
                            color: dragSnapshot.isDragging ? 'primary.contrastText' : 'inherit',
                            fontWeight: 600, 
                            verticalAlign: 'middle',
                            px: 1, // Reducir padding horizontal para dar espacio a los iconos
                            zIndex: dragSnapshot.isDragging ? 1000 : 2,
                            boxShadow: dragSnapshot.isDragging ? 6 : 0,
                            cursor: headCell.fixed ? 'default' : 'grab',
                            transition: dragSnapshot.isDragging ? 'none' : 'background-color 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              bgcolor: headCell.fixed ? 'background.paper' : 'grey.100', // Color sólido para evitar transparencia
                              boxShadow: (theme) => headCell.fixed ? 0 : `inset 0 0 0 2px ${theme.palette.primary.main}`,
                            },
                            ...dragProvided.draggableProps.style
                          }}
                        >
                          {headCell.sortable ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={() => onSort(headCell.id)}
                                sx={{ 
                                  flex: 1, 
                                  minWidth: 0, // Importante para que el flex-child pueda encogerse
                                  '& .MuiTableSortLabel-icon': {
                                    color: dragSnapshot.isDragging ? 'inherit !important' : 'inherit'
                                  }
                                }}
                              >
                                <Tooltip title={headCell.label} arrow placement="top">
                                  <Box component="span" sx={{ 
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    color: dragSnapshot.isDragging ? 'inherit !important' : 'inherit'
                                  }}>
                                    {headCell.label}
                                  </Box>
                                </Tooltip>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => onFilterClick(e, headCell.id)}
                                color={columnFilters[headCell.id] ? "primary" : "default"}
                                sx={{ color: dragSnapshot.isDragging ? 'inherit' : 'default' }}
                              >
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <span style={{ whiteSpace: 'nowrap' }}>{headCell.label}</span>
                          )}
                        </TableCell>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableRow>
              )}
            </Droppable>
          </TableHead>
          <TableBody>
            {results?.map((bitacora, index) => (
              <TableRow key={`row-${bitacora.ClaveBitacora || 'no-id'}-${index}`} hover>
                {localHeaderOrder.map(col => {
                  const cancelada = (bitacora?.ClaveEstatus === 11 || bitacora?.ClaveEstatus === "11");
                  // Un registro que trae UUID o ClaveFactura ya tiene factura,
                  // sin importar cómo llegó a esta tabla (p.ej. una consulta
                  // con ConTimbre=true). El guardia de "cancelada" no alcanza:
                  // el esquema con timbre no trae ClaveEstatus, así que sin
                  // esto ambos botones de acción quedarían activos sobre una
                  // factura que ya existe.
                  const yaFacturada = esRegistroYaFacturado(bitacora);

                  const val = bitacora[col.id];
                  let cellContent = val == null
                    ? "-"
                    : (typeof val === "object" ? JSON.stringify(val) : String(val));

                  if (col.id === 'acciones1') {
                    cellContent = yaFacturada ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <ReceiptIcon color="disabled" fontSize="small" />
                        <Typography variant="body2" component="span">Ya facturada</Typography>
                      </Box>
                    ) : cancelada ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <CancelIcon color="disabled" fontSize="small" />
                        <Typography variant="body2" component="span">Cancelada</Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<NoteAddIcon />}
                        onClick={() => onSelectForAdenda(bitacora)}
                        sx={{ minWidth: '130px', fontWeight: 'bold', zIndex: 0 }}
                      >
                        Crear Adenda
                      </Button>
                    );
                  } else if (col.id === 'acciones2') {
                    cellContent = (!cancelada && !yaFacturada) ? (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<ReceiptIcon />}
                        onClick={() => onSelectForFactura(bitacora)}
                        sx={{ minWidth: '140px', zIndex: 0 }}
                      >
                        Ingresar Factura
                      </Button>
                    ) : null;
                  } else if (['FechaUltimaModificacion', 'OrigenFechaHoraSalida', 'DestinoFechaHoraLlegada'].includes(col.id)) {
                    const fieldVal = bitacora[col.id];
                    cellContent = fieldVal ? new Date(fieldVal).toLocaleString() : "-";
                  }

                  return (
                    <TableCell key={col.id} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cellContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DragDropContext>
    </TableContainer>
  );
});

// Componente de item de columna memoizado
  // Simplificar item de arrastre para máxima fluidez
const DraggableColumnItem = memo(({ col, index, totalCount, onMove, onToggle, provided, snapshot }) => {
  return (
    <ListItem
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
        mb: 0.5,
        border: '1px solid',
        borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
        borderRadius: 1,
        // Usar transformaciones simples durante el arrastre
        boxShadow: snapshot.isDragging ? 4 : 0,
        zIndex: snapshot.isDragging ? 9999 : 1,
        transition: snapshot.isDragging ? 'none' : 'background-color 0.2s'
      }}
      secondaryAction={
        !col.fixed && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton 
                edge="end" 
                size="small" 
                onClick={() => onMove(index, -1)} 
                disabled={index === 0 || totalCount === 0} 
                sx={{ p: 0.2 }}
              >
                <ArrowUpwardIcon fontSize="inherit" />
              </IconButton>
              <IconButton 
                edge="end" 
                size="small" 
                onClick={() => onMove(index, 1)} 
                disabled={index === totalCount - 1} 
                sx={{ p: 0.2 }}
              >
                <ArrowDownwardIcon fontSize="inherit" />
              </IconButton>
            </Box>
            <Box {...provided.dragHandleProps} sx={{ cursor: 'grab', ml: 0.5, p: 1, display: 'flex', alignItems: 'center' }}>
              <DragHandleIcon fontSize="small" color="action" />
            </Box>
          </Box>
        )
      }
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Switch
          size="small"
          edge="start"
          checked={col.visible}
          disabled={col.disableToggle}
          onChange={() => onToggle(col.id)}
        />
      </ListItemIcon>
      <ListItemText 
        primary={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {col.label}
            {col.fixed && (
              <Box component="span" sx={{ bgcolor: 'action.disabledBackground', px: 1, borderRadius: 5, fontSize: '0.6rem', color: 'text.disabled', lineHeight: '1.2' }}>
                FIJA
              </Box>
            )}
          </Box>
        }
        primaryTypographyProps={{ variant: 'body2', noWrap: true, component: 'span' }}
        secondary={col.id}
        secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.65rem', opacity: 0.5 } }}
      />
    </ListItem>
  );
});
/**
 * Componente principal para la Consulta de Bitácoras y Remisiones.
 * Permite buscar registros en el API, y ofrece herramientas avanzadas de cliente para:
 * - Filtro de texto global dinámico
 * - Filtros estilo Excel por múltiples columnas
 * - Personalización de visibilidad y orden de las columnas (persistente)
 * - Ordenamiento ascendente/descendente de la tabla
 */
export default function Bitacoras() {
  const { auth } = useAuth();
  const { postConsulta, consultarEstatusFactura } = useSftfApi(auth);
  const [result, setResult] = useState(null);
  // Catálogo para el select de Estatus del buscador (I6): valores 0-4, según
  // EstatusFactura/Consultar. Se carga una sola vez al montar la pantalla.
  const [catalogoEstatus, setCatalogoEstatus] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryInfo, setRetryInfo] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const navigate = useNavigate();
  // Referencia a Formik para poder re-ejecutar la consulta desde el botón "Reintentar".
  const formikRef = useRef(null);

  // ==========================================
  // ESTADO Y CONFIGURACIÓN DE COLUMNAS / FILTROS
  // ==========================================
  // Persistencia en localStorage para que el usuario no pierda sus filtros al cambiar de pantalla
  const [filterText, setFilterText] = useState("");
  const [order, setOrder] = useState(() => localStorage.getItem('bitacoras_order') || 'desc');
  const [orderBy, setOrderBy] = useState(() => localStorage.getItem('bitacoras_orderBy') || 'FechaUltimaModificacion');
  const [columnFilters, setColumnFilters] = useState(() => JSON.parse(localStorage.getItem('bitacoras_columnFilters') || '{}'));

  // Columnas disponibles: las curadas, más las que aparezcan en la respuesta.
  const [columnasDisponibles, setColumnasDisponibles] = useState(
    () => construirColumnasBitacoras([], BITACORAS_DEFAULT_COLUMNS)
  );

  // Guarda la config CRUDA tal como está en localStorage (o la más reciente
  // que el propio usuario guardó). Al ampliar columnasDisponibles cuando
  // llegan resultados hay que re-fusionar contra esto, no contra el
  // columnsConfig en memoria: ese ya pudo haber descartado una columna
  // derivada que en su momento todavía no existía, y volver a fusionar
  // contra esa versión ya filtrada perdería la preferencia guardada del
  // usuario para esa columna en cuanto apareciera.
  const columnasGuardadasRef = useRef(null);

  // Estado para la configuración de columnas (visibilidad y orden)
  const [columnsConfig, setColumnsConfig] = useState(() => {
    let guardada = null;
    try {
      guardada = JSON.parse(localStorage.getItem('bitacoras_columnsConfig') || 'null');
    } catch (e) {
      guardada = null;
    }
    columnasGuardadasRef.current = guardada;
    return fusionarConfigColumnas(
      construirColumnasBitacoras([], BITACORAS_DEFAULT_COLUMNS),
      guardada
    );
  });

  const [dialogColumnsOpen, setDialogColumnsOpen] = useState(false);

  // Cargar el catálogo de Estatus para el select del buscador (I6). Un solo
  // intento con corte corto (ver consultarEstatusFactura): si el hub falla,
  // cae al respaldo hardcodeado, que ya cubre los 5 valores documentados.
  useEffect(() => {
    let cancelado = false;
    consultarEstatusFactura().then((data) => {
      if (!cancelado) setCatalogoEstatus(Array.isArray(data) ? data : []);
    });
    return () => {
      cancelado = true;
    };
  }, [consultarEstatusFactura]);

  // Estados para Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // Por defecto 50 filas es un buen equilibrio

  // Resetear la página a 0 cuando cambian los filtros, la búsqueda o el orden
  useEffect(() => {
    setPage(0);
  }, [filterText, columnFilters, order, orderBy]);

  // Cuando llegan resultados, se suman las columnas de los campos que la API
  // devuelve y no estaban modelados. Entran ocultas.
  useEffect(() => {
    if (!Array.isArray(result) || result.length === 0) return;

    const disponibles = construirColumnasBitacoras(result, BITACORAS_DEFAULT_COLUMNS);
    if (disponibles.length === columnasDisponibles.length) return;

    setColumnasDisponibles(disponibles);
    // Se re-fusiona contra la config CRUDA guardada (columnasGuardadasRef), no
    // contra columnsConfig en memoria: así una columna derivada que el
    // usuario ya había guardado como visible, pero que todavía no existía,
    // recupera su preferencia en cuanto aparece.
    setColumnsConfig(fusionarConfigColumnas(disponibles, columnasGuardadasRef.current));
  }, [result, columnasDisponibles.length]);

  // Memoizar activeColumns para que no cambie la referencia si columnsConfig es el mismo
  const activeColumns = useMemo(() => {
    return columnsConfig.filter(c => c.visible);
  }, [columnsConfig]);

  const saveColumnsConfig = useCallback((newConfig) => {
    setColumnsConfig(newConfig);
    const cruda = newConfig.map(c => ({ id: c.id, visible: c.visible }));
    columnasGuardadasRef.current = cruda;
    localStorage.setItem('bitacoras_columnsConfig', JSON.stringify(cruda));
  }, []);

  const handleRequestSort = useCallback((property) => {
    setOrder(prevOrder => {
      const isAsc = orderBy === property && prevOrder === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      localStorage.setItem('bitacoras_order', newOrder);
      return newOrder;
    });
    setOrderBy(property);
    localStorage.setItem('bitacoras_orderBy', property);
  }, [orderBy]);

  // Handler para clicks de navegación memoizado
  const handleSelectBitacora = useCallback((bitacora) => {
    navigate('/ingreso-factura', {
      state: {
        prepopulatedValues: {
          SerieBitacora: bitacora.SerieBitacora || "",
          FolioBitacora: bitacora.FolioBitacora || "",
          SerieRemision: bitacora.SerieRemision || "",
          FolioRemision: bitacora.FolioRemision || "",
          SerieFactura: bitacora.SerieFactura || "",
          FolioFactura: bitacora.FolioFactura || "",
          RemitenteRFC: bitacora.RemitenteRFC || "",
          Estatus: bitacora.Estatus || "",
          origen: bitacora.OrigenNombre || "",
          destino: bitacora.DestinoNombre || "",
          km: bitacora.TotalDistanciaRecorrida || "",
          conductor: bitacora.OperadorNombre || "",
          peso: bitacora.MercanciasPesoNetoTotal || "",
          mercanciaDescripcion: bitacora.MercanciaDescripcion || ""
        }
      }
    });
  }, [navigate]);

  const handleSelectBitacoraForAdenda = useCallback((bitacora) => {
    const claveBitacora = bitacora.ClaveBitacora != null ? String(bitacora.ClaveBitacora) : "";
    navigate('/adenda-bitacora', {
      state: {
        bitacoraData: {
          claveBitacora: claveBitacora,
          serieBitacora: bitacora.SerieBitacora || "",
          folioBitacora: bitacora.FolioBitacora != null ? String(bitacora.FolioBitacora) : "",
          origenNombre: bitacora.OrigenNombre || "",
          destinoNombre: bitacora.DestinoNombre || "",
          operadorNombre: bitacora.OperadorNombre || "",
          remitenteRFC: bitacora.RemitenteRFC || ""
        }
      }
    });
  }, [navigate]);

  const handleColumnFilterChange = (columnId, value) => {
    const newFilters = { ...columnFilters };
    if (value === null) {
      delete newFilters[columnId];
    } else {
      newFilters[columnId] = value;
    }
    setColumnFilters(newFilters);
    localStorage.setItem('bitacoras_columnFilters', JSON.stringify(newFilters));
  };

  // ==========================================
  // LOGICA DEL POPOVER ESTILO EXCEL (FILTRO POR COLUMNA)
  // ==========================================
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterId, setActiveFilterId] = useState(null);

  const [popoverSearchText, setPopoverSearchText] = useState("");
  const [popoverSelectedValues, setPopoverSelectedValues] = useState([]);

  /**
   * Extrae dinámicamente todos los valores únicos de la columna seleccionada
   * para pintarlos en los checkboxes del menú flotante de filtros.
   */
  const activeColumnUniqueValues = React.useMemo(() => {
    if (!result || !activeFilterId) return [];
    const values = result.map(bitacora => {
      let val = bitacora[activeFilterId];
      if (val === null || val === undefined) return "-";
      if (['FechaUltimaModificacion', 'OrigenFechaHoraSalida', 'DestinoFechaHoraLlegada'].includes(activeFilterId)) {
        return new Date(val).toLocaleString();
      }
      return String(val);
    });
    return [...new Set(values)].sort();
  }, [result, activeFilterId]);

  // Sincronizar el estado del Popover cuando se abre
  React.useEffect(() => {
    if (activeFilterId && filterAnchorEl) {
      let initialSelected = columnFilters[activeFilterId];
      if (!initialSelected || !Array.isArray(initialSelected)) {
        setPopoverSelectedValues(activeColumnUniqueValues);
      } else {
        setPopoverSelectedValues(initialSelected);
      }
      setPopoverSearchText("");
    }
  }, [activeFilterId, filterAnchorEl, columnFilters, activeColumnUniqueValues]);

  // Handler para popover memoizado para evitar re-render de la tabla
  const handleFilterClick = useCallback((event, id) => {
    event.stopPropagation();
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterId(id);
  }, []);

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterId(null);
  };

  const displayedCheckboxValues = React.useMemo(() => {
    if (!popoverSearchText) return activeColumnUniqueValues;
    const lowerFilter = popoverSearchText.toLowerCase();
    return activeColumnUniqueValues.filter(val => val.toLowerCase().includes(lowerFilter));
  }, [activeColumnUniqueValues, popoverSearchText]);

  const handleToggleAll = () => {
    if (popoverSelectedValues.length === activeColumnUniqueValues.length) {
      setPopoverSelectedValues([]); // Deselect all
    } else {
      setPopoverSelectedValues(activeColumnUniqueValues); // Select all
    }
  };

  const handleToggleValue = (val) => {
    const currentIndex = popoverSelectedValues.indexOf(val);
    const newSelected = [...popoverSelectedValues];
    if (currentIndex === -1) {
      newSelected.push(val);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    setPopoverSelectedValues(newSelected);
  };


  /**
   * Hook central de rendimiento. Entra en ejecución cuando la Data real de la API o
   * los parámetros de filtros sufren mutaciones para siempre mostrar la tabla actualizada.
   * Ejecuta en pipeline: [1. Filtro General] -> [2. Filtros por Columna] -> [3. Ordenamiento]
   */
  const filteredAndSortedResult = useMemo(() => {
    if (!result || !Array.isArray(result)) return [];
    let filtered = [...result];

    // 1. Filtrado de texto global sobre cualquier valor visible
    if (filterText) {
      const lowerFilter = filterText.toLowerCase();
      filtered = filtered.filter((bitacora) => {
        return activeColumns.some(col => {
          if (col.id === 'acciones1' || col.id === 'acciones2') return false;
          let val = bitacora[col.id];
          if (val === null || val === undefined) return false;

          const searchVal = String(val).toLowerCase();

          // Si es una columna de fecha, comprobamos su versión formateada
          if (['FechaUltimaModificacion', 'OrigenFechaHoraSalida', 'DestinoFechaHoraLlegada'].includes(col.id)) {
            try {
              const strDate = new Date(val).toLocaleString().toLowerCase();
              if (strDate.includes(lowerFilter)) return true;
            } catch (e) {}
          }

          return searchVal.includes(lowerFilter);
        });
      });
    }

    Object.entries(columnFilters).forEach(([colId, filterValues]) => {
      // Compatibilidad hacia atrás si hay strings guardados en lugar de arrays
      if (filterValues && Array.isArray(filterValues)) {
        filtered = filtered.filter((bitacora) => {
          let val = bitacora[colId];
          if (val === null || val === undefined) val = "-";

          let strVal;
          if (['FechaUltimaModificacion', 'OrigenFechaHoraSalida', 'DestinoFechaHoraLlegada'].includes(colId)) {
            strVal = val !== "-" ? new Date(val).toLocaleString() : "-";
          } else {
            strVal = String(val);
          }

          return filterValues.includes(strVal);
        });
      }
    });

    return stableSort(filtered, getComparator(order, orderBy));
  }, [result, filterText, order, orderBy, columnFilters, activeColumns]);

  const hasActiveFilters = Boolean(filterText) || Object.keys(columnFilters).length > 0;

  // Handler para reordenar columnas directamente desde la tabla
  const handleReorderColumnsFromTable = useCallback(async (sourceIdx, destIdx) => {
    setIsReordering(true);
    
    // Pequeño delay para permitir que la animación de la cabecera termine 
    // y mostrar el indicador de carga antes de procesar la tabla pesada.
    await new Promise(resolve => setTimeout(resolve, 300));

    setColumnsConfig(prev => {
      const activeIds = prev.filter(c => c.visible).map(c => c.id);
      const newActiveIds = Array.from(activeIds);
      const [movedId] = newActiveIds.splice(sourceIdx, 1);
      newActiveIds.splice(destIdx, 0, movedId);
      
      let activePointer = 0;
      const finalConfig = prev.map(col => {
        if (col.visible) {
          const nextActiveId = newActiveIds[activePointer++];
          return prev.find(c => c.id === nextActiveId);
        }
        return col;
      });

      const fixedChanged = finalConfig.some((col, idx) => col.fixed && prev[idx].id !== col.id);
      if (fixedChanged) return prev;

      const cruda = finalConfig.map(c => ({ id: c.id, visible: c.visible }));
      columnasGuardadasRef.current = cruda;
      localStorage.setItem('bitacoras_columnsConfig', JSON.stringify(cruda));
      return finalConfig;
    });
    
    setIsReordering(false);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilterText("");
    setColumnFilters({});
    localStorage.removeItem('bitacoras_columnFilters');
  }, []);

  /**
   * Exporta a Excel TODO lo que se muestra en la lista: todos los registros
   * filtrados y ordenados (no solo la página visible), usando únicamente las
   * columnas visibles y en su orden actual.
   */
  const handleExportExcel = useCallback(() => {
    exportBitacorasToExcel(filteredAndSortedResult, activeColumns);
  }, [filteredAndSortedResult, activeColumns]);




  return (
    // Layout ya desplaza el contenido con marginLeft segun el estado del drawer
    // (Layout.js: { xs: 0, md: collapsed ? '64px' : '280px' }), asi que aqui no
    // hay que volver a restar su ancho. Hacerlo dejaba la pagina en
    // calc(100vw - 400px), que en un telefono de 400px da cero y colapsaba todo
    // a una tira vertical de una letra por linea.
    <Container maxWidth={false} disableGutters sx={{ minWidth: 0 }}>
      <Typography variant="h4" gutterBottom>
        Consulta de Bitácora y/o Remisión
      </Typography>
      <Formik
        innerRef={formikRef}
        initialValues={{
          SerieBitacora: "",
          FolioBitacora: "",
          SerieRemision: "",
          FolioRemision: "",
          TipoFecha: "FechaCreacion",
          FechaInicio: "",
          FechaFin: "",
          ClaveBitacora: "",
          ConTimbre: "",
          Estatus: "",
        }}
        validationSchema={Yup.object({
          SerieBitacora: Yup.string(),
          FolioBitacora: Yup.string(),
          SerieRemision: Yup.string(),
          FolioRemision: Yup.string(),
          TipoFecha: Yup.string(),
          FechaInicio: Yup.string(),
          FechaFin: Yup.string(),
          ClaveBitacora: Yup.string(),
          ConTimbre: Yup.string(),
          Estatus: Yup.string(),
        })}
        onSubmit={async (values) => {
          try {
            setLoading(true);
            setError(null);
            setRetryInfo(null);

            // 1. Limpiamos filtros locales y reiniciamos página al iniciar una nueva búsqueda maestra
            setPage(0);
            setFilterText("");
            setColumnFilters({});
            localStorage.removeItem('bitacoras_columnFilters');

            // 2. Preparamos parámetros para la API
            const query = construirParamsConsulta(values);

            setLoading(true);
            setError(null);

            // 1. Llamamos a la API (con reintento automático ante fallos transitorios del hub)
            const opcionesConsulta = { onRetry: setRetryInfo };
            if (consultaSoloPorFiltrosNoSoportados(values)) {
              // El hub no responde estos filtros hoy; no vale la pena esperar
              // tres intentos completos para mostrar el error.
              opcionesConsulta.maxIntentos = 1;
            }
            let res = await postConsulta(query, opcionesConsulta);

            // 2. Filtramos localmente los resultados devueltos usando las Fechas
            if (res && Array.isArray(res)) {
              if (values.FechaInicio || values.FechaFin) {
                const start = values.FechaInicio ? new Date(`${values.FechaInicio}T00:00:00`) : null;
                const end = values.FechaFin ? new Date(`${values.FechaFin}T23:59:59`) : null;

                res = res.filter(bitacora => {
                  // Mapeamos el tipo de fecha a las propiedades que puede traer la API
                  let fechaStr = null;

                  if (values.TipoFecha === "FechaCreacion") {
                    // La API no envía FechaCreacion, usamos FechaUltimaModificacion
                    fechaStr = bitacora.FechaUltimaModificacion || bitacora.fechaUltimaModificacion;
                  } else if (values.TipoFecha === "FechaSalida") {
                    fechaStr = bitacora.OrigenFechaHoraSalida || bitacora.origenFechaHoraSalida;
                  } else if (values.TipoFecha === "FechaLlegada") {
                    fechaStr = bitacora.DestinoFechaHoraLlegada || bitacora.destinoFechaHoraLlegada;
                  }

                  // Fallback: si no encontró la que se pidió
                  if (!fechaStr) {
                    fechaStr = bitacora.FechaUltimaModificacion || bitacora.OrigenFechaHoraSalida || bitacora.origenFechaHoraSalida;
                  }

                  if (!fechaStr) return true; // Si definitivamente no hay fecha, la mostramos

                  const fechaBitacora = new Date(fechaStr);

                  let pass = true;
                  if (start && fechaBitacora < start) pass = false;
                  if (end && fechaBitacora > end) pass = false;

                  return pass;
                });
              }
            }
 
            setResult(res);
          } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || "Error desconocido al consultar bitácoras";
            setError(errorMsg);
            setResult(null);
          } finally {
            setLoading(false);
            setRetryInfo(null);
          }
        }}
      >
        <Form>
          {/* Dos filas de 12 columnas exactas. Antes las anchuras sumaban 25 sobre
              una rejilla de 12, asi que el reparto quedaba al azar y el select de
              Con Timbre se colapsaba hasta truncar su etiqueta. */}
          <Grid
            container
            columnSpacing={2}
            rowSpacing={3}
            sx={{
              alignItems: 'flex-start',
              // FormikTextField fija margin="normal" despues del spread de props,
              // asi que no se puede sobrescribir desde aqui. Se neutraliza en local
              // para que el ritmo lo marque el spacing de la rejilla y no el margen
              // de cada campo. El componente es compartido por cinco pantallas y no
              // se toca.
              '& .MuiFormControl-root': { my: 0 },
            }}
          >
            {/* Identificar el viaje */}
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <FormikTextField
                name="SerieBitacora"
                label="Serie Bitácora"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <FormikTextField
                name="FolioBitacora"
                label="Folio Bitácora"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormikTextField
                name="ClaveBitacora"
                label="Clave Bitácora"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormikTextField
                name="SerieRemision"
                label="Serie Remisión"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormikTextField
                name="FolioRemision"
                label="Folio Remisión"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Acotar el resultado */}
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormikTextField
                name="TipoFecha"
                label="Filtrar fecha por"
                select
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="FechaCreacion">Modificación</MenuItem>
                <MenuItem value="FechaSalida">Salida</MenuItem>
                <MenuItem value="FechaLlegada">Llegada</MenuItem>
              </FormikTextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <FormikTextField
                name="FechaInicio"
                label="Fecha Inicio"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <FormikTextField
                name="FechaFin"
                label="Fecha Fin"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormikTextField
                name="ConTimbre"
                label="Con Timbre"
                select
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="true">Sí (ya facturadas)</MenuItem>
                <MenuItem value="false">No (pendientes)</MenuItem>
              </FormikTextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormikTextField
                name="Estatus"
                label="Estatus"
                select
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                {catalogoEstatus.map((estatus) => (
                  <MenuItem
                    key={estatus.ClaveEstatusFactura}
                    value={String(estatus.ClaveEstatusFactura)}
                  >
                    {estatus.EstatusFactura}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Grid>
          </Grid>

          {/* El aviso va una sola vez y nombra los filtros afectados, en lugar de
              repetir el mismo texto bajo cuatro campos y dejar la fila despareja. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5 }}
          >
            Clave Bitácora, Serie y Folio Remisión y Estatus todavía no responden del lado de Cryoinfra.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{ mt: 2.5 }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Consultando..." : "Consultar"}
          </Button>
        </Form>
      </Formik>

      {error && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => formikRef.current?.submitForm()}
              disabled={loading}
            >
              REINTENTAR
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {(loading || isReordering) && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          bgcolor: 'rgba(255,255,255,0.7)', 
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            {isReordering
              ? "Reordenando columnas..."
              : retryInfo
                ? `Reintentando (${retryInfo.intento} de ${retryInfo.maxIntentos})...`
                : "Consultando bitácoras..."}
          </Typography>
        </Box>
      )}

      {result && result.length > 0 && (
        <Box sx={{ mt: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Resultados de la consulta
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Tooltip title="Exportar los resultados de la lista a Excel">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={filteredAndSortedResult.length === 0}
                  >
                    Exportar a Excel
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Configurar Columnas">
                <IconButton onClick={() => setDialogColumnsOpen(true)} color="primary">
                  <ViewColumnIcon />
                </IconButton>
              </Tooltip>
              {hasActiveFilters && (
                <Tooltip title="Limpiar todos los filtros">
                  <IconButton onClick={handleClearAllFilters} sx={{ color: 'action.active' }}>
                    <FilterAltOffIcon />
                  </IconButton>
                </Tooltip>
              )}
              <TextField
                size="small"
                placeholder="Buscar resultados..."
                variant="outlined"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ bgcolor: 'action.hover', px: 2, py: 0.5, borderRadius: 1 }}>
                Total: {filteredAndSortedResult.length} registro{filteredAndSortedResult.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          <BitacorasResultsTable 
            results={filteredAndSortedResult.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
            activeColumns={activeColumns}
            orderBy={orderBy}
            order={order}
            onSort={handleRequestSort}
            onFilterClick={handleFilterClick}
            onSelectForAdenda={handleSelectBitacoraForAdenda}
            onSelectForFactura={handleSelectBitacora}
            columnFilters={columnFilters}
            onReorderColumns={handleReorderColumnsFromTable}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 200]}
            component="div"
            count={filteredAndSortedResult.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas por página:"
            sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
          />
        </Box>
      )}

      {result && result.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No se encontraron resultados para la consulta realizada.
        </Alert>
      )}

      {result && result.length > 0 && filteredAndSortedResult?.length === 0 && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearAllFilters}>
              LIMPIAR FILTROS
            </Button>
          }
        >
          Hay filtros activos pero no hay registros que coincidan con la búsqueda.
        </Alert>
      )}

      {/* Popover global para el filtro estilo Excel */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Filtrar columna
          </Typography>
          <TextField
            autoFocus
            size="small"
            placeholder="Buscar..."
            variant="outlined"
            value={popoverSearchText}
            onChange={(e) => setPopoverSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={popoverSelectedValues.length === activeColumnUniqueValues.length && activeColumnUniqueValues.length > 0}
                    indeterminate={popoverSelectedValues.length > 0 && popoverSelectedValues.length < activeColumnUniqueValues.length}
                    onChange={handleToggleAll}
                  />
                }
                label="(Seleccionar Todo)"
              />
              {displayedCheckboxValues.map((val, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      size="small"
                      checked={popoverSelectedValues.indexOf(val) > -1}
                      onChange={() => handleToggleValue(val)}
                    />
                  }
                  label={val === "" ? "(Vacío)" : val}
                />
              ))}
            </FormGroup>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {popoverSelectedValues.length} de {activeColumnUniqueValues.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  handleColumnFilterChange(activeFilterId, null);
                  handleFilterClose();
                }}
              >
                Borrar
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (popoverSelectedValues.length === activeColumnUniqueValues.length) {
                    handleColumnFilterChange(activeFilterId, null);
                  } else {
                    handleColumnFilterChange(activeFilterId, popoverSelectedValues);
                  }
                  handleFilterClose();
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>

      <ColumnConfigDialog
        open={dialogColumnsOpen}
        onClose={() => setDialogColumnsOpen(false)}
        onApply={saveColumnsConfig}
        currentConfig={columnsConfig}
        columnasDisponibles={columnasDisponibles}
      />
    </Container>
  );
}
